/**
 * My Profile → Two-step sign-in.
 *
 * Self-enrolment, on purpose: the person who owns the phone types the number
 * and proves it with a code, so a mistyped digit can never become the only way
 * into an administrator account. Backup codes are shown exactly once, because
 * only their hashes are stored.
 *
 * Hidden entirely for roles that are not eligible, so ordinary staff never see
 * a control that does not apply to them.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  ShieldCheck, ShieldOff, Smartphone, Mail, Loader2, KeyRound, Copy, Check, AlertTriangle, Printer,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const errorOf = (err, fallback) =>
  err?.response?.data?.detail?.error || err?.response?.data?.detail || fallback;

export const TwoStepSignInCard = ({ isRTL }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [stage, setStage] = useState('idle'); // idle | mobile | code | codes | disable
  // null until the person chooses; defaults to whichever channel can actually
  // deliver, so an unconfigured SMS gateway is a fallback rather than a wall.
  const [channelChoice, setChannelChoice] = useState(null);
  const [sentChannel, setSentChannel] = useState(null);
  const [mobile, setMobile] = useState('');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState(null);
  const [maskedTarget, setMaskedTarget] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [copied, setCopied] = useState(false);
  const [password, setPassword] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/otp/status`);
      setStatus(response.data);
    } catch (err) {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Card data-testid="two-step-card-loading">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Not an eligible role: this control does not exist for them.
  if (!status || !status.eligible) return null;

  const smsReady = status.sms?.configured;
  // An administrator may have decided this already, in which case there is
  // nothing to offer: the server refuses a disagreeing request anyway.
  const channel = status.channel_locked
    ? status.channel_decided
    : (channelChoice || (smsReady ? 'sms' : 'email'));

  const startEnrolment = async () => {
    setError('');
    setBusy(true);
    try {
      const response = await axios.post(`${API_URL}/auth/otp/enroll/start`, {
        channel,
        ...(channel === 'sms' ? { mobile } : {}),
      });
      setChallengeId(response.data.challenge_id);
      setMaskedTarget(response.data.masked_target);
      setSentChannel(response.data.channel);
      setStage('code');
    } catch (err) {
      setError(errorOf(err, isRTL ? 'تعذر إرسال الرمز.' : 'Could not send the code.'));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnrolment = async () => {
    setError('');
    setBusy(true);
    try {
      const response = await axios.post(`${API_URL}/auth/otp/enroll/confirm`, {
        challenge_id: challengeId,
        code,
      });
      setBackupCodes(response.data.backup_codes || []);
      setStage('codes');
      setCode('');
      await load();
    } catch (err) {
      setError(errorOf(err, isRTL ? 'الرمز غير صحيح.' : 'That code is not correct.'));
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    setError('');
    setBusy(true);
    try {
      const response = await axios.post(`${API_URL}/auth/otp/backup-codes/regenerate`, { password });
      setBackupCodes(response.data.backup_codes || []);
      setPassword('');
      setStage('codes');
      await load();
    } catch (err) {
      setError(errorOf(err, isRTL ? 'كلمة المرور غير صحيحة.' : 'That password is not correct.'));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setError('');
    setBusy(true);
    try {
      await axios.post(`${API_URL}/auth/otp/disable`, { password });
      setPassword('');
      setStage('idle');
      await load();
    } catch (err) {
      setError(errorOf(err, isRTL ? 'كلمة المرور غير صحيحة.' : 'That password is not correct.'));
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = async () => {
    await navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card data-testid="two-step-signin-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          {status.enabled ? (
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
          ) : (
            <ShieldOff className="h-5 w-5 text-muted-foreground" />
          )}
          {isRTL ? 'تسجيل الدخول بخطوتين' : 'Two-step sign-in'}
          {status.enabled ? (
            <Badge className="bg-emerald-100 text-emerald-700" data-testid="two-step-status-badge">
              {isRTL ? 'مُفعّل' : 'On'}
            </Badge>
          ) : (
            <Badge variant="outline" data-testid="two-step-status-badge">
              {isRTL ? 'غير مُفعّل' : 'Off'}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          {status.enabled && status.channel === 'email'
            ? (isRTL
              ? 'رمز من ٦ أرقام يُرسل إلى بريدك الإلكتروني عند تسجيل الدخول. يُطبَّق على حسابات المسؤولين فقط.'
              : 'A 6-digit code sent to your email when you sign in. Applies to administrator accounts only.')
            : (isRTL
              ? 'رمز من ٦ أرقام يُرسل إلى جوالك عند تسجيل الدخول. يُطبَّق على حسابات المسؤولين فقط.'
              : 'A 6-digit code sent to your phone when you sign in. Applies to administrator accounts only.')}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <div
            className="p-3 rounded-lg text-sm border border-red-200 bg-red-50 text-red-700"
            data-testid="two-step-error"
          >
            {error}
          </div>
        )}

        {status.paused && (
          <div
            className="p-3 rounded-lg text-sm border border-amber-200 bg-amber-50 text-amber-800 flex items-start gap-2"
            data-testid="two-step-paused-notice"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {isRTL
                ? 'أوقف مسؤول أعلى طلب الرمز مؤقتاً على مستوى النظام، لذلك لن يُطلب منك رمز حالياً.'
                : 'A Super Admin has paused two-step sign-in system-wide, so you will not be asked for a code at the moment.'}
            </span>
          </div>
        )}

        {!smsReady && (
          <div
            className="p-3 rounded-lg text-sm border border-amber-200 bg-amber-50 text-amber-800 flex items-start gap-2"
            data-testid="two-step-sms-not-configured"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {isRTL
                ? 'بوابة الرسائل النصية غير مهيأة، لذلك سيُرسل رمز التحقق إلى بريدك الإلكتروني.'
                : 'The SMS gateway is not configured, so the verification code will be sent to your email instead.'}
            </span>
          </div>
        )}

        {status.enabled && stage === 'idle' && (
          <div className="space-y-4" data-testid="two-step-enabled-panel">
            <div className="flex items-center gap-3 text-sm">
              {status.channel === 'email'
                ? <Mail className="h-4 w-4 text-muted-foreground" />
                : <Smartphone className="h-4 w-4 text-muted-foreground" />}
              <span className="text-muted-foreground">
                {status.channel === 'email'
                  ? (isRTL ? 'البريد الإلكتروني' : 'Email')
                  : (isRTL ? 'الجوال' : 'Phone')}
              </span>
              <span className="font-medium" dir="ltr" data-testid="two-step-masked-mobile">
                {status.masked_target}
              </span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <KeyRound className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                {isRTL ? 'رموز الاحتياط المتبقية' : 'Backup codes left'}
              </span>
              <span className="font-medium" data-testid="two-step-backup-remaining">
                {status.backup_codes_remaining} / {status.backup_codes_total}
              </span>
            </div>
            <Separator />
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setStage('regenerate'); setError(''); }}
                data-testid="two-step-regenerate-codes"
              >
                <KeyRound className="h-4 w-4 mr-2" />
                {isRTL ? 'رموز احتياط جديدة' : 'Get new backup codes'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  await axios.post(`${API_URL}/auth/otp/trusted-devices/revoke`);
                  await load();
                }}
                data-testid="two-step-revoke-devices"
              >
                {isRTL
                  ? `إلغاء المتصفحات الموثوقة (${status.trusted_devices})`
                  : `Forget trusted browsers (${status.trusted_devices})`}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700"
                onClick={() => setStage('disable')}
                data-testid="two-step-turn-off"
              >
                {isRTL ? 'إيقاف' : 'Turn off'}
              </Button>
            </div>
          </div>
        )}

        {stage === 'regenerate' && (
          <div className="space-y-3" data-testid="two-step-regenerate-panel">
            <Label htmlFor="two-step-regenerate-password">
              {isRTL
                ? 'أكّد كلمة المرور لإصدار رموز جديدة'
                : 'Confirm your password to issue a new set'}
            </Label>
            <Input
              id="two-step-regenerate-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              data-testid="two-step-regenerate-password-input"
              dir="ltr"
            />
            <p className="text-xs text-muted-foreground">
              {isRTL
                ? 'ستتوقف كل الرموز القديمة عن العمل فوراً.'
                : 'Every code from your old sheet stops working immediately.'}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={regenerate}
                disabled={busy || !password}
                data-testid="two-step-regenerate-confirm"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {isRTL ? 'إصدار رموز جديدة' : 'Issue new codes'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setStage('idle'); setPassword(''); setError(''); }}
              >
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
            </div>
          </div>
        )}

        {stage === 'disable' && (
          <div className="space-y-3" data-testid="two-step-disable-panel">
            <Label htmlFor="two-step-password">
              {isRTL ? 'أكّد كلمة المرور لإيقاف الميزة' : 'Confirm your password to turn this off'}
            </Label>
            <Input
              id="two-step-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              data-testid="two-step-password-input"
              dir="ltr"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={disable} disabled={busy || !password} data-testid="two-step-disable-confirm">
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {isRTL ? 'إيقاف الميزة' : 'Turn off'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setStage('idle'); setPassword(''); setError(''); }}>
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
            </div>
          </div>
        )}

        {!status.enabled && stage === 'idle' && (
          <Button
            onClick={() => setStage('mobile')}
            data-testid="two-step-enable"
          >
            <ShieldCheck className="h-4 w-4 mr-2" />
            {isRTL ? 'تفعيل تسجيل الدخول بخطوتين' : 'Turn on two-step sign-in'}
          </Button>
        )}

        {stage === 'mobile' && (
          <div className="space-y-3" data-testid="two-step-mobile-panel">
            {smsReady && !status.channel_locked && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={channel === 'sms' ? 'default' : 'outline'}
                  onClick={() => setChannelChoice('sms')}
                  data-testid="two-step-channel-sms"
                >
                  <Smartphone className="h-4 w-4 mr-2" />
                  {isRTL ? 'رسالة نصية' : 'Text message'}
                </Button>
                <Button
                  size="sm"
                  variant={channel === 'email' ? 'default' : 'outline'}
                  onClick={() => setChannelChoice('email')}
                  data-testid="two-step-channel-email"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  {isRTL ? 'البريد الإلكتروني' : 'Email'}
                </Button>
              </div>
            )}

            {status.channel_locked && (
              <div
                className="p-3 rounded-lg border bg-muted/30 text-sm flex items-start gap-2"
                data-testid="two-step-channel-locked"
              >
                {channel === 'sms'
                  ? <Smartphone className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                  : <Mail className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />}
                <span className="text-muted-foreground">
                  {isRTL
                    ? `حدّد المسؤول أن يصلك الرمز عبر ${channel === 'sms' ? 'رسالة نصية' : 'البريد الإلكتروني'}.`
                    : `Your administrator has set your codes to arrive by ${channel === 'sms' ? 'text message' : 'email'}.`}
                </span>
              </div>
            )}

            {channel === 'sms' ? (
              <>
                <Label htmlFor="two-step-mobile">
                  {isRTL ? 'رقم الجوال السعودي' : 'Saudi mobile number'}
                </Label>
                <Input
                  id="two-step-mobile"
                  value={mobile}
                  onChange={(event) => setMobile(event.target.value)}
                  placeholder="0548080608"
                  data-testid="two-step-mobile-input"
                  dir="ltr"
                />
                <p className="text-xs text-muted-foreground">
                  {isRTL
                    ? 'سنرسل رمزاً لهذا الرقم. لن يُحفظ الرقم قبل التحقق منه.'
                    : 'We will text a code to this number. It is not saved until the code comes back.'}
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="two-step-email-target">
                {isRTL
                  ? 'سيُرسل الرمز إلى بريد حسابك الإلكتروني.'
                  : "The code will be sent to your account's email address."}
              </p>
            )}
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={startEnrolment}
                disabled={busy || (channel === 'sms' && !mobile)}
                data-testid="two-step-send-code"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {isRTL ? 'إرسال الرمز' : 'Send code'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setStage('idle'); setError(''); }}>
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
            </div>
          </div>
        )}

        {stage === 'code' && (
          <div className="space-y-3" data-testid="two-step-code-panel">
            <Label htmlFor="two-step-code">
              {isRTL
                ? `أدخل الرمز المُرسل إلى ${maskedTarget}`
                : `Enter the code sent to ${maskedTarget}`}
            </Label>
            {sentChannel === 'email' && (
              <p className="text-xs text-muted-foreground">
                {isRTL ? 'تحقق من مجلد البريد غير المرغوب إذا لم تجده.' : "Check your spam folder if it isn't there."}
              </p>
            )}
            <Input
              id="two-step-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="000000"
              maxLength={6}
              inputMode="numeric"
              className="text-xl tracking-[0.4em] text-center font-semibold"
              data-testid="two-step-code-input"
              dir="ltr"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={confirmEnrolment} disabled={busy || code.length < 6} data-testid="two-step-confirm-code">
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {isRTL ? 'تأكيد' : 'Confirm'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setStage('mobile'); setCode(''); setError(''); }}>
                {isRTL ? 'رجوع' : 'Back'}
              </Button>
            </div>
          </div>
        )}

        {stage === 'codes' && (
          <div className="space-y-3" data-testid="two-step-backup-codes-panel">
            <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                {isRTL
                  ? 'هذه الرموز تُعرض مرة واحدة فقط. اطبعها أو احفظها في مكان آمن — كل رمز يعمل مرة واحدة، ويُستخدم إذا فُقد الهاتف أو تعطلت خدمة الرسائل.'
                  : 'These are shown once only. Print them or keep them somewhere safe — each one works a single time, and they are how you sign in if the phone is lost or the SMS service is down.'}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 font-mono text-sm" dir="ltr">
              {backupCodes.map((backupCode, index) => (
                <div
                  key={backupCode}
                  className="px-3 py-2 rounded border bg-muted/40"
                  data-testid={`two-step-backup-code-${index}`}
                >
                  {backupCode}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={copyCodes} data-testid="two-step-copy-codes">
                {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                {copied ? (isRTL ? 'تم النسخ' : 'Copied') : (isRTL ? 'نسخ الرموز' : 'Copy codes')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="two-step-print-codes">
                <Printer className="h-4 w-4 mr-2" />
                {isRTL ? 'طباعة' : 'Print'}
              </Button>
              <Button size="sm" onClick={() => { setStage('idle'); setBackupCodes([]); }} data-testid="two-step-codes-done">
                {isRTL ? 'حفظتها' : 'I have saved them'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TwoStepSignInCard;
